import type { JSX } from 'react';
import { Fragment, useEffect, useState } from 'react';
import { Alert, Button, Center, Group, Loader, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, scheduleApi, type PlanView, type ScheduleView } from '../../api';
import { formatDate, UNIT_DAYS } from '../../format';
import { buildOutline } from './outline';
import { condense } from './condense';
import { calendarOf } from './localSchedule';
import { type Paper, printOn, setPaper, storedPaper } from '../../export/paper';

const DAY = 86_400_000;
const t = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);

/**
 * The schedule for paper or PDF (AP-14, decision R-12): the browser prints
 * it, "Save as PDF" makes the file. A table with the bars in percent of the
 * page width, so the whole plan always fits across - unlike the screen view,
 * whose chart is as wide as it needs. Landscape A4, phases repeat nothing,
 * a row never breaks across pages.
 */
export function SchedulePrintPage(): JSX.Element {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const asked = search.get('format');
  const simple = search.get('ansicht') === 'vereinfacht';
  const [paper, choose] = useState<Paper>(asked === 'A3' || asked === 'A4' ? asked : storedPaper());
  useEffect(() => setPaper(paper), [paper]);
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const plan = useQuery({ queryKey: ['plan', projectId], queryFn: () => scheduleApi.plan(projectId) });
  const schedule = useQuery({ queryKey: ['schedule', projectId], queryFn: () => scheduleApi.schedule(projectId) });
  if (plan.isPending || schedule.isPending) return <Center h={200}><Loader /></Center>;
  if (plan.isError || schedule.isError) return <Alert color="red">Der Terminplan konnte nicht geladen werden.</Alert>;
  const project = projects.data?.find((p) => p.id === projectId);
  return (
    <div className={`pst-print-page pst-paper-${paper}`}>
      <Group justify="space-between" className="pst-no-print" mb="sm">
        <Button variant="subtle" size="xs" onClick={() => navigate(`/projekte/${projectId}/termine`)}>← Zurück zum Terminplan</Button>
        <Group gap="xs">
          <Text size="xs" c="dimmed">Im Druckdialog „Als PDF speichern“ wählen; Format und Querformat sind voreingestellt.</Text>
          <SegmentedControl size="xs" value={simple ? 'simple' : 'detail'} onChange={(v) => setSearch((prev) => { const next = new URLSearchParams(prev); if (v === 'simple') next.set('ansicht', 'vereinfacht'); else next.delete('ansicht'); return next; })} data={[{ value: 'detail', label: 'Detailliert' }, { value: 'simple', label: 'Vereinfacht' }]} />
          <SegmentedControl size="xs" value={paper} onChange={(v) => choose(v as Paper)} data={[{ value: 'A4', label: 'A4 quer' }, { value: 'A3', label: 'A3 quer' }]} />
          <Button size="xs" onClick={() => printOn(paper)}>Drucken / PDF</Button>
        </Group>
      </Group>
      <ScheduleSheet title={project ? `${project.number} · ${project.name}` : 'Terminplan'} plan={plan.data} schedule={schedule.data} simple={simple} />
    </div>
  );
}

/** The printable schedule itself - also a sheet of the Mappe. */
export function ScheduleSheet({ title, plan, schedule: full, simple = false }: { title: string; plan: PlanView; schedule: ScheduleView; simple?: boolean }): JSX.Element {
  // The simplified plan shows each summary as one bar (Zusammenfassungen).
  const s = simple ? condense(full, [], calendarOf(plan)).schedule : full;
  const rows = buildOutline(s.groups, s.tasks, new Set());
  const from = t(s.projectStart);
  const to = Math.max(t(s.earliestFinish), t(s.targetFinish)) + DAY;
  const x = (iso: string): number => ((t(iso) - from) / (to - from)) * 100;
  const w = (a: string, b: string): number => Math.max(0.4, ((t(b) - t(a) + DAY) / (to - from)) * 100);
  // Month ticks along the top.
  const months: { label: string; left: number }[] = [];
  for (let d = new Date(from); d.getTime() < to; d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1))) {
    const first = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
    const left = Math.max(0, ((first - from) / (to - from)) * 100);
    // A label needs room: skip one that would sit on top of the previous.
    if (months.length === 0 || left - months[months.length - 1]!.left >= 7) {
      months.push({ label: d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit', timeZone: 'UTC' }), left });
    }
  }

  return (
    <div>
      <Stack gap={2} mb="xs">
        <Title order={4}>{title}</Title>
        <Text size="xs" c="dimmed">
          {plan.name}{simple ? ' (vereinfacht)' : ''} · {formatDate(s.projectStart)} – {formatDate(s.earliestFinish)} · Ziel {formatDate(s.targetFinish)} · gedruckt {formatDate(new Date().toISOString())}
        </Text>
      </Stack>
      <table className="pst-print-table">
        <thead>
          <tr>
            <th style={{ width: '6%' }}>Nr.</th>
            <th style={{ width: '30%' }}>Vorgang</th>
            <th style={{ width: '7%' }}>Beginn</th>
            <th style={{ width: '7%' }}>Ende</th>
            <th style={{ width: '4%', textAlign: 'right' }}>{UNIT_DAYS}</th>
            <th style={{ width: '6%' }}>Verantw.</th>
            <th style={{ position: 'relative' }}>
              {months.map((m) => <span key={m.left} style={{ position: 'absolute', left: `${m.left}%`, top: 2, fontWeight: 400, fontSize: 9 }}>{m.label}</span>)}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.id}>
              {r.kind === 'group' ? (
                <tr className="pst-print-group">
                  <td>{r.group.code}</td>
                  <td style={{ paddingLeft: r.depth * 8 }}>{r.group.name}</td>
                  <td>{formatDate(r.group.earlyStart)}</td>
                  <td>{formatDate(r.group.earlyFinish)}</td>
                  <td style={{ textAlign: 'right' }}>{r.group.durationDays}</td>
                  <td />
                  <td className="pst-print-bars">
                    {r.group.earlyStart && r.group.earlyFinish && <div className="pst-print-bar group" style={{ left: `${x(r.group.earlyStart)}%`, width: `${w(r.group.earlyStart, r.group.earlyFinish)}%` }} />}
                  </td>
                </tr>
              ) : (
                <tr>
                  <td>{r.task.code}</td>
                  <td style={{ paddingLeft: r.depth * 8 }}>{r.task.name}</td>
                  <td>{formatDate(r.task.earlyStart)}</td>
                  <td>{r.task.durationDays === 0 ? '' : formatDate(r.task.earlyFinish)}</td>
                  <td style={{ textAlign: 'right' }}>{r.task.durationDays === 0 ? '◆' : r.task.durationDays}</td>
                  <td>{r.task.owner}</td>
                  <td className="pst-print-bars">
                    {r.task.earlyStart && r.task.earlyFinish && (r.task.durationDays === 0
                      ? <div className="pst-print-milestone" style={{ left: `${x(r.task.earlyStart)}%` }} />
                      : <div className={`pst-print-bar${r.task.critical ? ' critical' : ''}${r.task.status === 'done' ? ' done' : ''}`} style={{ left: `${x(r.task.earlyStart)}%`, width: `${w(r.task.earlyStart, r.task.earlyFinish)}%` }} />)}
                    <div className="pst-print-target" style={{ left: `${x(s.targetFinish)}%` }} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      <Text size="xs" c="dimmed" mt="xs">Balken rot: kritischer Pfad · grau: erledigt · ◆ Meilenstein · senkrechte Linie: Zieltermin. Termine in Arbeitstagen ({UNIT_DAYS}) nach dem Projektkalender.</Text>
    </div>
  );
}
